export const DEFAULT_CUSTOMERS_LIST_FILTERS = {
  name: "",
  code: "",
  isDeleted: "",
};

export const CUSTOMERS_PAGE_CONSTANTS = {
  NAME_FIELD: "name",
  CUSTOMER_ID_FIELD: "customerId",
  PAGE_TITLE: "Customers",
  PAGE_SUBTITLE: "Tenant provisioning and status.",
  ADD_CUSTOMER_LABEL: "Add customer",
  ADD_CUSTOMER_DRAWER_TITLE: "Create customer",
  ADD_CUSTOMER_NAME_PLACEHOLDER: "Customer name",
  ADD_CUSTOMER_ID_PLACEHOLDER: "Customer ID",
  ADD_CUSTOMER_TIMEZONE_PLACEHOLDER: "Select time zone",
  customerExistsValidationFailedError:
    "Unable to verify whether this customer name or ID already exists.",
  DETAIL_CANCEL_LABEL: "Cancel",
  DETAIL_APPLY_LABEL: "Apply",
  TABLE_TITLE: "All customers",
  CUSTOMER_TITLE_FALLBACK: "Customer",
  STATUS_ACTIVE: "Active",
  STATUS_INACTIVE: "Inactive",
  STATUS_FILTER_OPTIONS: [
    { label: "Active", value: "false" },
    { label: "Inactive", value: "true" },
  ],
  INACTIVE_DIALOG_MESSAGE:
    "This customer is inactive, so its configuration cannot be opened.",
  INACTIVE_DIALOG_OK_LABEL: "Ok",
  CLEAR_FILTERS_LABEL: "Clear filters",
  EMPTY_STATE_TITLE: "No customers match these filters",
  EMPTY_STATE_HINT: "Clear one or more filters to widen the search.",
  DEFAULT_PAGE_SIZE: 25,
  COLUMNS: {
    NAME: "Customer name",
    CODE: "Customer ID",
    STATUS: "Status",
  },
};

export const hasActiveCustomersListFilters = (filters = {}) =>
  Object.keys(DEFAULT_CUSTOMERS_LIST_FILTERS).some(
    (key) => String(filters?.[key] ?? "").trim() !== ""
  );

export const buildCustomersResultSummary = (
  shownCount,
  filtersActive,
  grandTotal
) => {
  const shown = Number(shownCount) || 0;
  const total = Number(grandTotal) || 0;
  if (filtersActive) {
    if (total > 0) {
      return `${shown} of ${total} customers match`;
    }
    return `${shown} customers match`;
  }
  return `${shown} customers`;
};

export const normalizeCustomersListFilters = ({
  name = "",
  code = "",
  isDeleted = "",
} = {}) => ({
  name: String(name).trim(),
  code: String(code).trim(),
  isDeleted: String(isDeleted).trim(),
});

export const GROUP_ACCESS_DRAWER_TAB_KEYS = {
  DATA_CONNECTIONS: "dataConnections",
  PAGE_ACCESS_CONTROL: "pageAccessControl",
};

export const GROUP_ACCESS_DRAWER_TABS = [
  {
    value: GROUP_ACCESS_DRAWER_TAB_KEYS.DATA_CONNECTIONS,
    label: "Data connections",
  },
  {
    value: GROUP_ACCESS_DRAWER_TAB_KEYS.PAGE_ACCESS_CONTROL,
    label: "Entitlements",
  },
];

export const GROUPS_FACILITIES_PICKER = {
  DEFAULT_PAGE: 0,
  PAGE_SIZE: 50,
  SEARCH_DEBOUNCE_MS: 250,
  LISTBOX_SCROLL_THRESHOLD_PX: 24,
};

export const GROUPS_LIST_PAGINATION = {
  DEFAULT_PAGE: 0,
  DEFAULT_PAGE_SIZE: 25,
};

export const GROUP_ID_TYPES = {
  customerId: "Customer ID",
  groupId: "Group ID",
};

export const TAG_SOURCE_CONNECTORS = {
  MQTT_SPB: "mqtt-spb",
  HACHWIMS: "hachwims",
  WM_MLENGINE: "wm-mlengine",
  WM_APP: "wm-app",
};

export const GROUPS_CONSTANTS = {
  createGroupLables: {
    CUSTOMER_NAME: "Customer name",
    TIME_ZONE: "Time zone",
    GROUP_ID: "Customer ID",
  },
  createGroupButtons: {
    SAVE: "Save",
    CANCEL: "Cancel",
  },
  breadcrumb: {
    CUSTOMER_OVERVIEW: "Customer Overview",
    CREATE_CUSTOMER: "Create Customer",
  },
  alert: {
    TITLE: "Alert",
    CUSTOMER_CREATED_SUCCESS: "Customer created successfully",
  },
  toast: {
    TYPE_SUCCESS: "success",
    TYPE_FAILURE: "failure",
    TITLE_SUCCESS: "Success",
    TITLE_FAILED: "Failed",
    DURATION: 10000,
    MESSAGE_FAILURE: "Please try again after some time",
  },
  navigation: {
    CUSTOMERS_PATH: "/customers",
  },
  groupsList: {
    TITLE: "Customer Overview",
    ADD_NEW_CUSTOMER: " Add New Customer",
    SEARCH_PLACEHOLDER: "Search Group..",
    ACTIVATE_CUSTOMER: "Activate customer",
    DEACTIVATE_CUSTOMER: "Deactivate customer",
    ACTIVATE_CONFIRM_MESSAGE: "Are you sure you want to activate this customer?",
    DEACTIVATE_CONFIRM_MESSAGE:
      "You are about to deactivate 1 customer. This action will affect all associated data.\n\nAre you sure you want to proceed?",
    BUTTON_ACTIVATE: "Activate",
    BUTTON_DEACTIVATE: "Deactivate",
    ARIA_LABEL_VIEW_GROUP: "View Group",
    ARIA_LABEL_EDIT_GROUP: "Edit Group",
    DATA_CONNECTIONS_UPDATE_SUCCESS: "Updated successfully",
  },
  addEditGroup: {
    CREATE_NEW_CUSTOMER: "Create New Customer",
    UPDATE_CUSTOMER: "Update Customer",
    CUSTOMER_PREVIEW: "Customer Preview",
    EDIT_CUSTOMER: "Edit Customer",
    CUSTOMER_EDIT: "Customer Edit",
    CUSTOMER_LABEL: "Customer: ",
    ACTIVE_LABEL: "Active",
    ACTIVATE_KAFKA_TOPIC: "Activate Kafka Topic",
    DEACTIVATE_KAFKA_TOPIC: "Deactivate Kafka Topic",
    ACTIVATE_TOPIC_CONFIRM: "Are you sure you want to activate this topic?",
    DEACTIVATE_TOPIC_CONFIRM: "Are you sure you want to deactivate this topic?",
    KAFKA_TAB_KEY: "Kafka",
    KAFKA_TAB_TITLE: "KAFKA TOPICS",
    SCHEMAS_TAB_KEY: "Schemas",
    SCHEMAS_TAB_TITLE: "SCHEMAS",
    TOPIC_ACTIVE_ALT: "Topic Active",
    TOPIC_INACTIVE_ALT: "Topic InActive",
  },
  groups: {
    NO_CUSTOMERS_FOUND: "No customers found",
    TOAST_ERROR_TITLE: "Error",
  },
  groupAccessDrawer: {
    TAB_KEYS: GROUP_ACCESS_DRAWER_TAB_KEYS,
    TABS: GROUP_ACCESS_DRAWER_TABS,
    COLUMN_HEADERS: {
      MODULE_NAME: "Module Name",
      ACCESS: "Access",
    },
    ACCESS_COLUMN_TOOLTIP: "Turn off module access for the selected customer",
    APPLY: "Apply",
    DATA_CONNECTIONS: {
      MQTT_SPB_TITLE: TAG_SOURCE_CONNECTORS.MQTT_SPB,
      MQTT_SPB_FIELD_LABEL: "Group ID",
      MQTT_SPB_PLACEHOLDER: "Group ID",
      MQTT_SPB_ARIA_LABEL: TAG_SOURCE_CONNECTORS.MQTT_SPB,
      MQTT_SPB_TOGGLE_ARIA: "Enable or disable mqtt-spb group id",
      HACHWIMS_TITLE: TAG_SOURCE_CONNECTORS.HACHWIMS,
      HACHWIMS_FIELD_LABEL: "Facility/Plant",
      HACHWIMS_PLACEHOLDER: "Facility/Plant",
      HACHWIMS_ARIA_LABEL: TAG_SOURCE_CONNECTORS.HACHWIMS,
      HACHWIMS_TOGGLE_ARIA: "Enable or disable hachwims facilities",
    },
  },
  loading: {
    FALLBACK: "Loading...",
  },
  createGroupErrorMessages: {
    groupId: "Only alphanumeric characters, dash (-) and underscore (_) are allowed",
    idTooShort: "{idType} must be at least 3 characters",
    idTooLong: "{idType} cannot exceed 20 characters.",
    idDuplicate: "{idType} already exists",
    idRequired: "{idType} is required",
    name: "Customer already exists",
    nameRequired: "Customer name is required",
    timeZoneRequired: "Time zone is required",
    facilitiesRequired: "Select atleast one facility to proceed",
  },
  createGroup: {
    ERROR_TYPE_REQUIRED: "required",
    ERROR_TYPE_MANUAL: "manual",
  },
};

export default GROUPS_CONSTANTS;
